import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from './ui/alert-dialog';
import { CalendarIcon, SkipForward, Clock } from 'lucide-react';

export interface ConflictSlot {
  startDateTime: string;
  endDateTime: string;
}

export interface ConflictResolution {
  original: ConflictSlot;
  action: 'skip' | 'reschedule';
  newStartDateTime?: string;
  newEndDateTime?: string;
}

interface ConflictResolutionDialogProps {
  open: boolean;
  onClose: () => void;
  conflicts: ConflictSlot[];
  programTitle: string;
  onResolve: (resolutions: ConflictResolution[]) => void;
}

export function ConflictResolutionDialog({
  open,
  onClose,
  conflicts,
  programTitle,
  onResolve,
}: ConflictResolutionDialogProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resolutions, setResolutions] = useState<ConflictResolution[]>([]);
  const [mode, setMode] = useState<'choose' | 'reschedule'>('choose');
  const [newDate, setNewDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('');

  const current = conflicts[currentIndex];
  if (!current) return null;

  const conflictStart = new Date(current.startDateTime);
  const conflictEnd = new Date(current.endDateTime);
  const duration = conflictEnd.getTime() - conflictStart.getTime();

  const formatDate = (d: Date) =>
    d.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const formatTime = (d: Date) =>
    d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });

  const handleSkip = () => {
    const updated = [...resolutions, { original: current, action: 'skip' as const }];
    advance(updated);
  };

  const handleRescheduleMode = () => {
    // Pre-fill with the original date/time
    const localStart = new Date(current.startDateTime);
    setNewDate(localStart.toISOString().split('T')[0]);
    setNewStartTime(localStart.toTimeString().slice(0, 5));
    setMode('reschedule');
  };

  const handleConfirmReschedule = () => {
    if (!newDate || !newStartTime) return;

    const startDt = new Date(`${newDate}T${newStartTime}:00`);
    const endDt = new Date(startDt.getTime() + duration);

    const updated = [
      ...resolutions,
      {
        original: current,
        action: 'reschedule' as const,
        newStartDateTime: startDt.toISOString(),
        newEndDateTime: endDt.toISOString(),
      },
    ];
    advance(updated);
  };

  const advance = (updated: ConflictResolution[]) => {
    setResolutions(updated);
    setMode('choose');
    setNewDate('');
    setNewStartTime('');
    if (currentIndex + 1 < conflicts.length) {
      setCurrentIndex(currentIndex + 1);
    } else {
      onResolve(updated);
      setCurrentIndex(0);
      setResolutions([]);
    }
  };

  const handleSkipAll = () => {
    const remaining = conflicts.slice(currentIndex).map((c) => ({
      original: c,
      action: 'skip' as const,
    }));
    const all = [...resolutions, ...remaining];
    onResolve(all);
    setCurrentIndex(0);
    setResolutions([]);
    setMode('choose');
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <AlertDialogContent className="bg-gray-800 border-gray-600 rounded-2xl shadow-2xl max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-white flex items-center gap-2">
            <CalendarIcon className="size-5 text-amber-400" />
            Schedule Conflict ({currentIndex + 1} of {conflicts.length})
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-gray-400">
            <span className="font-medium text-white">"{programTitle}"</span> conflicts with an existing program on:
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4 my-2">
          <p className="text-white font-medium">{formatDate(conflictStart)}</p>
          <p className="text-gray-400 text-sm flex items-center gap-1.5 mt-1">
            <Clock className="size-3.5" />
            {formatTime(conflictStart)} – {formatTime(conflictEnd)}
          </p>
        </div>

        {mode === 'choose' ? (
          <div className="space-y-3 mt-2">
            <Button
              onClick={handleRescheduleMode}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              <CalendarIcon className="size-4" />
              Change date & time
            </Button>
            <Button
              onClick={handleSkip}
              variant="outline"
              className="w-full border-gray-600 bg-transparent text-white hover:bg-gray-700 gap-2"
            >
              <SkipForward className="size-4" />
              Skip this instance
            </Button>
            {conflicts.length - currentIndex > 1 && (
              <Button
                onClick={handleSkipAll}
                variant="ghost"
                className="w-full text-gray-400 hover:text-white hover:bg-gray-700"
              >
                Skip all remaining ({conflicts.length - currentIndex})
              </Button>
            )}
            <Button
              onClick={onClose}
              variant="ghost"
              className="w-full text-gray-500 hover:text-gray-300 hover:bg-gray-700/50"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-gray-300 text-sm mb-1.5 block">New Date</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="bg-gray-900 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300 text-sm mb-1.5 block">Start Time</Label>
              <Input
                type="time"
                value={newStartTime}
                onChange={(e) => setNewStartTime(e.target.value)}
                className="bg-gray-900 border-gray-600 text-white"
              />
            </div>
            {newDate && newStartTime && (
              <p className="text-sm text-gray-400 flex items-center gap-1.5">
                <Clock className="size-3.5" />
                End time: {formatTime(new Date(new Date(`${newDate}T${newStartTime}:00`).getTime() + duration))}
                <span className="text-gray-500 ml-1">({Math.round(duration / 60000)} min)</span>
              </p>
            )}
            <div className="flex gap-2">
              <Button
                onClick={() => setMode('choose')}
                variant="outline"
                className="flex-1 border-gray-600 bg-transparent text-white hover:bg-gray-700"
              >
                Back
              </Button>
              <Button
                onClick={handleConfirmReschedule}
                disabled={!newDate || !newStartTime}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                Confirm
              </Button>
            </div>
          </div>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
